import { useState, useRef, useEffect } from "react";
import { X, Send, Sparkles, Mic, MicOff, Volume2, VolumeX, Zap, Sunrise, Paperclip, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { trackEvent } from "@/components/BehaviorTracker";
import useFileUpload from "@/hooks/useFileUpload";
import DropOverlay from "@/components/aria/DropOverlay";
import MessageBubble from "@/components/aria/MessageBubble";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
const synth = window.speechSynthesis;

const QUICK_COMMANDS = [
  "What can you do?",
  "Score the yacht gala gallery and build a reel",
  "Generate a highlight reel for gallery-yacht-gala-2025",
  "Reprocess all photos in gallery-yacht-gala-2025",
  "Deliver the yacht gala gallery to the client",
  "How's the business this week?",
  "Move the latest booking to confirmed",
  "Create an event called Sunset Yacht Soirée on 2025-12-15 in Kingston",
  "Cancel event event-abc12345",
  "Send the event-photography contract to the latest booking",
];

export default function AriaChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Welcome to Aristocrats JA. I'm ARIA, your personal concierge. How can I help you today?" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [listening, setListening] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  // `briefing` here is kept as a flag — null on reset/close. The actual
  // payload consumed by BriefingCard rides on the message itself
  // (msg.briefing) so React's reconciler doesn't double-mount the card.
  const [briefing, setBriefing] = useState(null);
  const [briefingLoading, setBriefingLoading] = useState(false);
  const messagesEnd = useRef(null);
  const recognitionRef = useRef(null);
  const voiceEnabledRef = useRef(false);

  // Voice synthesis with bold-content preservation + ACTION-JSON stripping
  const speakText = (text) => {
    if (!synth || (!voiceEnabled && !voiceEnabledRef.current)) return;
    synth.cancel();
    const clean = text
      .replace(/\$\$ACTION:.*$/gm, "")
      .replace(/\*\*([^*]+)\*\*/g, "$1");
    const utterance = new SpeechSynthesisUtterance(clean);
    utterance.rate = 0.95; utterance.pitch = 1.1; utterance.volume = 0.8;
    const voices = synth.getVoices();
    const preferred = voices.find(v => v.name.includes("Female") || v.name.includes("Samantha") || v.name.includes("Google UK English Female"));
    if (preferred) utterance.voice = preferred;
    utterance.onstart = () => setSpeaking(true);
    utterance.onend = () => setSpeaking(false);
    utterance.onerror = () => setSpeaking(false);
    synth.speak(utterance);
  };

  const toggleVoice = () => {
    if (voiceEnabled) { synth?.cancel(); setSpeaking(false); }
    setVoiceEnabled(!voiceEnabled);
  };

  // File upload + drag-and-drop hook
  const upload = useFileUpload({ sessionId, setSessionId, setMessages, speakText });

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    voiceEnabledRef.current = voiceEnabled;
  }, [voiceEnabled]);

  // Morning / Wrap-Up briefing fetch on first panel-open per kind per day
  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    const adminEmail = localStorage.getItem("auth_email") || "admin";
    const wasAdmin = !!token;
    setIsAdmin(wasAdmin);
    if (!wasAdmin || !open) return;

    // Pick morning vs wrap-up based on Kingston local time (UTC-5).
    // 20:00–04:59 Kingston → wrap-up; 05:00–19:59 Kingston → morning.
    const utcHour = new Date().getUTCHours();
    const kingstonHour = (utcHour - 5 + 24) % 24;
    const kind = (kingstonHour >= 20 || kingstonHour < 5) ? "wrap-up" : "morning";

    const dayKey = `aria_briefing_${kind}_${adminEmail}_${new Date().toISOString().slice(0, 10)}`;
    if (sessionStorage.getItem(dayKey)) return;
    setBriefingLoading(true);
    const endpoint = kind === "wrap-up" ? "/aria/wrap-up-briefing" : "/aria/morning-briefing";
    axios.get(`${API}${endpoint}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        sessionStorage.setItem(dayKey, "1");  // mark only on success
        setBriefing(res.data);
        setMessages(prev => [
          ...prev,
          { role: "assistant", content: res.data.summary, isBriefing: true, briefing: res.data }
        ]);
        if (voiceEnabledRef.current && synth) {
          setTimeout(() => speakText(res.data.summary), 250);
        }
      })
      .catch(() => { /* admin-only — silent on 403 */ })
      .finally(() => setBriefingLoading(false));
  }, [open]);

  // ---- Voice listening ----
  const startListening = () => {
    if (!SpeechRecognition) return;
    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = (event) => { setInput(event.results[0][0].transcript); setListening(false); };
    recognition.onerror = () => setListening(false);
    recognition.onend = () => setListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  };

  const stopListening = () => { recognitionRef.current?.stop(); setListening(false); };

  // ---- Send a chat message ----
  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMsg }]);
    setLoading(true);

    try {
      const headers = {};
      const token = localStorage.getItem("auth_token");
      if (token) headers["Authorization"] = `Bearer ${token}`;

      // Track chat topic for behavioral intelligence
      const keywords = userMsg.toLowerCase();
      if (keywords.includes("wedding") || keywords.includes("photo") || keywords.includes("event")) trackEvent("chat_topic", "events_photography", "chat");
      else if (keywords.includes("website") || keywords.includes("app") || keywords.includes("software")) trackEvent("chat_topic", "tech_services", "chat");
      else if (keywords.includes("price") || keywords.includes("cost") || keywords.includes("budget")) trackEvent("chat_topic", "pricing", "chat");
      else if (keywords.includes("book") || keywords.includes("reserve") || keywords.includes("available")) trackEvent("chat_topic", "booking", "chat");
      else if (keywords.includes("cologne") || keywords.includes("perfume") || keywords.includes("suit") || keywords.includes("beard")) trackEvent("chat_topic", "products", "chat");
      else trackEvent("chat_topic", "general_inquiry", "chat");

      const res = await axios.post(`${API}/chat`, { message: userMsg, session_id: sessionId }, { headers });
      setSessionId(res.data.session_id);
      const reply = res.data.response;

      setMessages(prev => [...prev, {
        role: "assistant",
        content: reply,
        actionResult: res.data.action_result || null,
        chauffeurIntent: res.data.chauffeur_intent || null,
      }]);
      speakText(reply);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "I appreciate your patience. Please try again momentarily, or reach us at aristocratsja@gmail.com." }]);
    } finally {
      setLoading(false);
    }
  };

  const voiceState = {
    voiceEnabled, setVoiceEnabled, voiceEnabledRef,
    speaking, synthAvailable: !!synth,
  };

  return (
    <>
      {/* Floating Button */}
      <AnimatePresence>
        {!open && (
          <motion.button
            data-testid="aria-chat-toggle"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            onClick={() => setOpen(true)}
            className="fixed bottom-24 right-6 z-[2147483647] w-14 h-14 rounded-full bg-gold flex items-center justify-center shadow-[0_4px_24px_rgba(212,175,55,0.3)] hover:bg-gold-light hover:scale-105 transition-all"
          >
            <Sparkles size={22} className="text-black" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="aria-chat-panel"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-24 right-6 z-[2147483647] w-[400px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-6rem)] flex flex-col overflow-hidden border border-white/10 shadow-2xl"
            style={{ background: "rgba(8,8,8,0.97)", backdropFilter: "blur(24px)" }}
            onDragEnter={upload.handlePanelDragEnter}
            onDragLeave={upload.handlePanelDragLeave}
            onDragOver={upload.handlePanelDragOver}
            onDrop={upload.handlePanelDrop}
          >
            <DropOverlay active={upload.dragActive} />

            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 bg-white/[0.02]">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-gold/20 flex items-center justify-center">
                  <Sparkles size={16} className="text-gold" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-white text-sm font-medium font-body">ARIA</p>
                    {isAdmin && (
                      <span className="flex items-center gap-1 text-[9px] font-body px-1.5 py-0.5 bg-gold/15 text-gold border border-gold/20">
                        <Zap size={8} /> Agent Mode
                      </span>
                    )}
                  </div>
                  <p className="text-white/30 text-[10px] font-body">{isAdmin ? "Autonomous Admin Agent — I run the platform for you" : "Luxury Concierge"}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {synth && (
                  <button data-testid="aria-voice-toggle" onClick={toggleVoice} className={`transition-colors ${voiceEnabled ? (speaking ? "text-gold animate-pulse" : "text-gold") : "text-white/30 hover:text-white/50"}`}>
                    {voiceEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
                  </button>
                )}
                <button data-testid="aria-chat-close" onClick={() => {
                  setOpen(false);
                  synth?.cancel();
                  // Drop any briefing messages on close so reopening produces a clean slate.
                  setMessages(prev => prev.filter(m => !m.isBriefing));
                  setBriefing(null);
                }} className="text-white/30 hover:text-white transition-colors">
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Admin Quick Commands */}
            {isAdmin && messages.length <= 2 && (
              <div className="px-4 py-2 border-b border-white/5 bg-gold/[0.03]">
                <p className="text-white/20 text-[9px] font-body uppercase tracking-wider mb-1.5">Try saying...</p>
                <div className="flex flex-wrap gap-1">
                  {QUICK_COMMANDS.map(cmd => (
                    <button key={cmd} onClick={() => setInput(cmd)} className="text-[9px] font-body px-2 py-1 bg-white/5 text-white/30 hover:text-gold hover:bg-gold/10 transition-colors" data-testid={`quick-cmd-${cmd.split(" ").slice(0,3).join("-").toLowerCase().replace(/[^a-z0-9-]/g, "")}`}>
                      {cmd}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {briefingLoading && (
                <div className="flex items-center gap-2 text-gold/60 text-xs font-body" data-testid="briefing-loading">
                  <Sunrise size={12} className="animate-pulse" /> Generating your morning briefing...
                </div>
              )}
              {messages.map((msg, i) => (
                <MessageBubble
                  key={msg.placeholderId || `msg-${i}`}
                  msg={msg}
                  voiceState={voiceState}
                  onSpeak={speakText}
                  onSuggestionClick={(prompt) => setInput(prompt)}
                />
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white/[0.04] px-4 py-3">
                    <div className="flex gap-1">
                      <span className="w-1.5 h-1.5 bg-gold/50 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="w-1.5 h-1.5 bg-gold/50 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="w-1.5 h-1.5 bg-gold/50 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEnd} />
            </div>

            {/* Input */}
            <div className="px-4 py-3 border-t border-white/10 bg-white/[0.02]">
              <div className="flex items-center gap-2">
                <input
                  ref={upload.fileInputRef}
                  type="file"
                  className="hidden"
                  accept="image/jpeg,image/png,image/webp,image/gif,application/pdf,text/plain,text/csv,text/markdown,.md"
                  onChange={upload.handleFilePicked}
                  data-testid="aria-upload-file-input"
                />
                <button
                  data-testid="aria-upload-btn"
                  onClick={() => upload.fileInputRef.current?.click()}
                  disabled={upload.uploading || loading}
                  title="Upload an image or document for ARIA to analyze"
                  className={`p-2.5 transition-all ${upload.uploading ? "bg-gold/20 text-gold animate-pulse" : "bg-white/5 text-white/30 hover:text-gold hover:bg-gold/10"} disabled:opacity-40`}
                >
                  {upload.uploading ? <Loader2 size={16} className="animate-spin" /> : <Paperclip size={16} />}
                </button>
                {SpeechRecognition && (
                  <button data-testid="aria-voice-btn" onClick={listening ? stopListening : startListening} disabled={loading} className={`p-2.5 transition-all ${listening ? "bg-red-500/20 text-red-400 animate-pulse" : "bg-white/5 text-white/30 hover:text-gold hover:bg-gold/10"} disabled:opacity-40`}>
                    {listening ? <MicOff size={16} /> : <Mic size={16} />}
                  </button>
                )}
                <input
                  data-testid="aria-chat-input"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && sendMessage()}
                  placeholder={listening ? "Listening..." : upload.uploading ? "Uploading..." : isAdmin ? "Command ARIA..." : "Ask ARIA anything..."}
                  className="flex-1 bg-white/5 border border-white/10 text-white text-sm px-4 py-2.5 focus:outline-none focus:border-gold/40 placeholder:text-white/20 font-body"
                />
                <button data-testid="aria-chat-send" onClick={sendMessage} disabled={loading || !input.trim()} className="bg-gold hover:bg-gold-light text-black p-2.5 transition-colors disabled:opacity-40">
                  <Send size={16} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
